// cosmos.wgsl.ts — the hero. A drifting nebula with a star field, parallaxed by
// the pointer and bent into a tunnel when the reader opens. Hand-written WGSL;
// kept in a .ts so the bundler and tsc never argue about loaders.

export const COSMOS_WGSL = /* wgsl */ `
fn hash21(p : vec2<f32>) -> f32 {
  var q = fract(p * vec2<f32>(123.34, 345.45));
  q += dot(q, q + 34.345);
  return fract(q.x * q.y);
}

fn hash22(p : vec2<f32>) -> vec2<f32> {
  let n = sin(dot(p, vec2<f32>(41.0, 289.0)));
  return fract(vec2<f32>(262144.0, 32768.0) * n);
}

// value noise + a little smoothing
fn vnoise(p : vec2<f32>) -> f32 {
  let i = floor(p);
  let f = fract(p);
  let u = f * f * (3.0 - 2.0 * f);
  let a = hash21(i + vec2<f32>(0.0, 0.0));
  let b = hash21(i + vec2<f32>(1.0, 0.0));
  let c = hash21(i + vec2<f32>(0.0, 1.0));
  let d = hash21(i + vec2<f32>(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

fn fbm(p0 : vec2<f32>) -> f32 {
  var p = p0;
  var sum = 0.0;
  var amp = 0.5;
  let m = mat2x2<f32>(1.6, 1.2, -1.2, 1.6);
  for (var i = 0; i < 6; i = i + 1) {
    sum = sum + amp * vnoise(p);
    p = m * p;
    amp = amp * 0.5;
  }
  return sum;
}

// Scattered stars on a tiled grid, with twinkle.
fn stars(uv : vec2<f32>, density : f32, t : f32) -> f32 {
  let gv = fract(uv * density) - 0.5;
  let id = floor(uv * density);
  var acc = 0.0;
  for (var y = -1; y <= 1; y = y + 1) {
    for (var x = -1; x <= 1; x = x + 1) {
      let offs = vec2<f32>(f32(x), f32(y));
      let rnd = hash22(id + offs);
      let pos = offs + rnd - 0.5;
      let d = length(gv - pos);
      let bright = smoothstep(0.06, 0.0, d);
      let twink = 0.5 + 0.5 * sin(t * (1.5 + rnd.x * 3.0) + rnd.y * 30.0);
      acc = acc + bright * twink * step(0.72, rnd.x);
    }
  }
  return acc;
}

fn render(uv : vec2<f32>, frag : vec2<f32>) -> vec3<f32> {
  let res = G.resolution;
  // aspect-correct, centered coordinates
  var p = (frag - 0.5 * res) / max(res.y, 1.0);

  let t = G.time * 0.06;

  // pointer parallax — drift the field toward the cursor
  let par = G.pointer * 0.12;
  p += par;

  // reader-open "warp": pull everything into a tunnel toward center
  let r = length(p);
  let warp = G.warp;
  let tunnel = mix(1.0, 1.0 / (r * 2.2 + 0.35), warp);
  var q = p * tunnel;
  let ang = atan2(q.y, q.x) + t * 1.4 + warp * 2.0;
  q = vec2<f32>(cos(ang), sin(ang)) * length(q);

  // nebula via domain-warped fbm
  let drift = vec2<f32>(t * 1.3, -t * 0.7);
  let w = vec2<f32>(fbm(q * 1.5 + drift), fbm(q * 1.5 - drift + 5.2));
  let n = fbm(q * 2.3 + w * 1.8 + drift);

  // colour ramp — deep indigo -> magenta -> warm cream highlights
  let c1 = vec3<f32>(0.04, 0.05, 0.12);
  let c2 = vec3<f32>(0.35, 0.10, 0.42);
  let c3 = vec3<f32>(0.92, 0.45, 0.55);
  let c4 = vec3<f32>(0.98, 0.93, 0.84);
  var col = mix(c1, c2, smoothstep(0.25, 0.6, n));
  col = mix(col, c3, smoothstep(0.55, 0.82, n));
  col = mix(col, c4, smoothstep(0.82, 0.97, n) * 0.7);

  // vignette toward the edges keeps text legible
  let vig = smoothstep(1.25, 0.2, length(p));
  col *= mix(0.55, 1.0, vig);

  // star layers (parallaxed differently for depth)
  let s1 = stars(p * 2.0 + par * 2.0, 8.0, G.time);
  let s2 = stars(p * 1.0 + par * 1.0, 4.0, G.time * 0.7);
  col += vec3<f32>(0.9, 0.95, 1.0) * (s1 * 0.6 + s2 * 0.9);

  // subtle scanline-free grain so flat areas never band
  let g = hash21(frag + vec2<f32>(G.time, -G.time)) - 0.5;
  col += g * 0.015;

  // dim slightly as the reader text fades in
  col *= mix(1.0, 0.45, warp * 0.7);

  return max(col, vec3<f32>(0.0));
}
`;
